
import 'url-search-params-polyfill'

import isObject from 'lodash.isobject'

import env from 'env'
import events from '../../events/bus'

import { TokenManager } from './token.manager'
import { AccessToken, Prompt, State, Workflow } from './state'

import { OIDC_CONFIG_KEY, WEB_MESSAGE_RESPONSE_TYPE, SECOND, AUTHORITY_ORIGINS, CRYPTOSTORAGE_SECRET_KEY } from './constants'
import { HTTP_METHOD, OidcData, OidcServerConfig, TokenData, UserProfile } from './constants/types'

import HiddenIframe, { CheckSession, isIframe } from './utils/iframe'
import { Popup, PopupWindow, Config as PopupConfig } from './utils/navigator'
import { WebCryptoStorage } from './utils/storage.crypto'
import { wait } from './utils'

import { wrap } from '../../utils'
import { LocalStorage } from '../../utils/storage'

import { EventData } from './session'

import { useToken } from '../api'

import { AuthenticationError, LoginRequiredError, HiddenIframeTimeout, MissingTokenError, AccessTokenRequestError } from '../../errors'

import {
  SDK_AUTH_STATUS_CHANGE,
  SDK_AUTH_SESSION_CHANGED,
  SDK_OIDC_TOKEN_FETCH_START,
  SDK_OIDC_TOKEN_FETCH_END
} from '../../events/types/sdk.events'

import { ResponseMode } from '../../sdk'

export type OidcClientConfig = {
  authority: string,
  clientId: string | number,
  callbackUrl?: string
  origin?: string,
  responseMode?: ResponseMode,
  responseType?: ResponseType
  scope?: string,
  workflow?: Workflow,
  state?: 'permanent' | 'session',
  secret?: string
}

export type RequestSubscriptionConfig = {
  url: string,
  cdn: string
}


export class OidcClient {
  public readonly VERSION = '1.2.0'
  public shouldCheckSession: boolean
  public readAt: Date = new Date
  public readonly config: OidcClientConfig
  public state?: State
  public idServerConfig?: OidcServerConfig

  protected requestSubscription?: RequestSubscriptionConfig

  protected readonly session: CheckSession

  private isTokenFetching: boolean

  static get configEndpoint(): string {
    return '.well-known/openid-configuration'
  }

  get events() {
    return events
  }

  get isAuthorized() {
    return !!this.state?.isLoggedIn
  }

  get isTokenReady() {
    return new Promise((resolve => {
      if (!this.isTokenFetching) {
        return resolve(true)
      } else {
        this.events.addEventListener(SDK_OIDC_TOKEN_FETCH_END.type, resolve, false)
      }
    }))
  }

  constructor(config: OidcClientConfig, checkSession = false) {
    this.config = config
    this.shouldCheckSession = checkSession
    this.session = new CheckSession(checkSession ? 5 * SECOND : 0)

    this.read = this.read.bind(this)
    this.checkSession = this.checkSession.bind(this)
    this.postSignInProcess = this.postSignInProcess.bind(this)
    this.createState = this.createState.bind(this)
    this.loadState = this.loadState.bind(this)

    this.isTokenFetching = false
  }

  async createState(options = { permanent: this.config.state === 'permanent', key: this.config.secret || CRYPTOSTORAGE_SECRET_KEY }) {
    const config = await this.fetchConfig()

    if (this.state) return this.state

    return this.state = new State({
      authority: this.config.authority,
      authorization_endpoint: config.authorization_endpoint,
      end_session_endpoint: config.end_session_endpoint,
      callback_url: this.config.callbackUrl,
      client_id: this.config.clientId,
      scope: this.config.scope || [...config.scopes_supported || []].join(' '),
      response_type: this.config.responseType || 'code',
      response_mode: this.config.responseMode || 'query',
      cryptoStorage: new WebCryptoStorage(options.key, 1.2, options.permanent)
    })
  }


  public async loadState() {
    await this.createState()
    await this.state?.load()
    await Promise.all([
      this.checkSession(),
      this.postSignInProcess()
    ])
  }

  public fetch() {
    if (this.isTokenFetching) return this.isTokenReady
    return wrap(this.postSignInProcess())
  }

  /** * * * * * * * * * * * * * * * * * * * * * * * *
   *            Token Management
   * * * * * * * * * * * * * * * * *  * * * * * * * */

  private async postSignInProcess() {
    if (!this.state) throw new AuthenticationError('No state')

    const config = this.idServerConfig as OidcServerConfig

    const manager = new TokenManager({
      callbackUrl: this.config.callbackUrl as string,
      clientId: this.config.clientId as string,
      endpoint: config.token_endpoint as string
    }, this.state)

    this.isTokenFetching = true
    this.events.dispatchEvent(SDK_OIDC_TOKEN_FETCH_START)

    // Make sure we have fresh tokens
    const token = await manager.fetchToken()

    // Update token
    this.state.update({
      data: this.state.data,
      token
    })

    useToken(token.access_token)

    this.isTokenFetching = false
    this.events.dispatchEvent(SDK_OIDC_TOKEN_FETCH_END)

    // Save data
    await this.state.save()

    this.readAt = new Date
  }


  /** * * * * * * * * * * * * * * * * * * * * * * * *
   *            Session Management
   * * * * * * * * * * * * * * * * *  * * * * * * * */

  private async checkSession() {
    if (!this.state || !this.shouldCheckSession) return false
    let lastStatus = true

    const { check_session_iframe: url, issuer } = this.idServerConfig as OidcServerConfig

    const clientId = this.config.clientId as string
    const sessionState = this.state.data.session_state

    const sessionHandler = async (event: CustomEvent<EventData>) => {
      if (event.detail.status === 'changed') events.dispatchEvent(SDK_AUTH_SESSION_CHANGED)
      if (lastStatus !== event.detail.isActive) {
        events.dispatchEvent(SDK_AUTH_STATUS_CHANGE(event.detail.isActive))

        lastStatus = event.detail.isActive

        if (!event.detail.isActive) this.clear()
      }
    }

    if (clientId && sessionState) {
      const session = this.session.use(clientId, sessionState, url, issuer)

      try {
        await session.mount()
        session.check()

        // @ts-expect-error: allow custom type
        session.iframe?.addEventListener<string>('session:status', sessionHandler, false)
      } catch (error) {
        console.warn('Check session is disabled. Hidden iframe mount error', error)
        session.unmount()
        return false
      }
    }

    return false
  }

  /** * * * * * * * * * * * * * * * * * * * * * * * *
   *            Data Management
   * * * * * * * * * * * * * * * * *  * * * * * * * */

  public async read() {
    if (!this.state) {
      throw new AuthenticationError('No State')
    }

    await this.state.load()

    const jwt = this.state.jwt as AccessToken

    return {
      legacyGUID: jwt.legacy_uguid,
      legacyToken: jwt.legacy_token,
      email: jwt.email as string,
      userId: jwt.sub as string,
      token: this.state.token as TokenData
    } as OidcData
  }

  /** * * * * * * * * * * * * * * * * * * * * * * * *
   *            Redirect Sign In
   * * * * * * * * * * * * * * * * *  * * * * * * * */

  private async makeRedirect(prompt?: Prompt) {
    if (!this.state) throw new AuthenticationError('No State')

    const { url } = await this.state.createURL('page', this.config.workflow, prompt)
    this.state.createSessionTransaction('redirect')

    location.href = url

    // Fix error screen blink
    await wait(2000)
  }

  public async signInWithSilentRedirect() {
    let redirect = false
    try {
      // Check curent session
      await this.loadState()
    } catch (error) {
      if (error instanceof AuthenticationError) {
        redirect = true
      } else {
        throw error
      }
    }

    if (redirect && this.state) {
      await this.makeRedirect('none')
    }
  }

  public async signInWithRedirect(silent = false) {
    let redirect = false
    try {
      // Check curent session
      await this.loadState()
    } catch (error) {
      if (error instanceof AuthenticationError) {
        redirect = true
      } else {
        throw error
      }
    }

    if (redirect && this.state) {
      if (!silent) {
        await this.makeRedirect()
      } else {
        const isDone = await this.isSilentLoginDone()
        if (!isDone) await this.makeRedirect()
      }
    }
  }

  public async handleRedirectCallback(bypass = false, path?: string) {
    try {
      await this.createState()

      if (this.state) {
        this.state.finishSessionTransaction()

        // Handle silent iframe
        const payload = { type: WEB_MESSAGE_RESPONSE_TYPE, response: this.state.data }
        window.parent.postMessage(payload, this.config.origin || location.origin)

        /**
         * If we are not in iframe - we should receive token, save it and redirect to the path
         * If we are in iframe - we should skip this step, because postSignInProcess will be called from parent window
         */
        if (!isIframe()) {
          await this.postSignInProcess()
          await this.state.save()

          const route = this.state.transaction?.path || path || '/' as string

          if (!bypass && typeof route === 'string') {
            location.replace(route)
          }
        }
      }
    } catch (error) {
      console.error('OIDC redirect callback error. ' + error.message)
      throw error
    }
  }

  /** * * * * * * * * * * * * * * * * * * * * * * * *
   *            Popup Sign In
   * * * * * * * * * * * * * * * * *  * * * * * * * */

  private async showPopup(popup?: Popup | PopupWindow | PopupConfig, workflow?: Workflow, prompt?: Prompt) {
    if (!this.state) throw new AuthenticationError('No state')

    popup = popup instanceof PopupWindow ? popup : new PopupWindow({ cdn: '', service: '', ...popup })

    const { url } = await this.state.createURL('popup', workflow || this.config.workflow, prompt)
    const data = await popup.open(url, this.state.id)

    this.state.test(data)

    this.state.data = {
      code_verifier: this.state.data.code_verifier,
      session_state: data.session_state,
      state: data.state,
      code: data.code
    }

    await Promise.allSettled([
      this.postSignInProcess(),
      this.shouldCheckSession && this.checkSession()
    ])

    return popup
  }

  public async signInWithPopup(silent: boolean, popup?: Popup | PopupWindow | PopupConfig, workflow?: Workflow, prompt?: Prompt) {
    try {
      // Try to restore active session
      await this.loadState()
    } catch (error) {
      if (!(error instanceof AuthenticationError)) {
        throw error
      } else await this.createState()
    }

    if (silent) {
      const isDone = await this.isSilentLoginDone()
      if (isDone) return popup instanceof Popup ? popup : null
    }
    return await this.showPopup(popup, workflow, prompt)
  }

  public async handlePopupCallback(): Promise<void> {
    try {
      await this.createState()

      if (this.state) {
        this.state.handlePopupSession()

        const sender = !isIframe() ?
          window.parent.opener || window.opener || window.parent :
          window.parent

        if (sender && sender.postMessage) {
          const payload = { type: WEB_MESSAGE_RESPONSE_TYPE, response: this.state.data }
          sender.postMessage(payload, this.config.origin || location.origin)
        } else {
          throw new Error('Popup is blocked. Broken session')
        }
      }
    } catch (error) {
      console.error(error.message)
    }
  }

  /** * * * * * * * * * * * * * * * * * * * * * * * *
   *            Silent Sign In
   * * * * * * * * * * * * * * * * *  * * * * * * * */

  private async isSilentLoginDone() {
    try {
      await this.silentSignIn()
      return true
    } catch {
      return false
    }
  }

  public async silentSignIn() {
    const iframe = new HiddenIframe()
    try {
      this.createState()
      if (!this.state) throw new AuthenticationError('No state')

      const { url } = await this.state.createURL('page', undefined, 'none')

      this.state.createSessionTransaction('web_message')

      const data = await iframe.open(url, this.state.id)

      this.state.data = { ...this.state.data, session_state: data.session_state, state: data.state }

      iframe.close()

      if (data && !!data.error) throw new LoginRequiredError('Login Required')

      this.state.test(data)
      this.state.data = { code_verifier: this.state.data.code_verifier, ...data }

      await Promise.allSettled([
        this.postSignInProcess(),
        this.shouldCheckSession && this.checkSession(),
        this.state.closeSessionTransaction()
      ])

      this.session.check()
    } catch (error) {
      iframe.close()
      if (error instanceof AccessTokenRequestError) {
        if (this.state?.data) {
          this.state.data = {}

        }
        this.state?.clear()
      } else if (error instanceof LoginRequiredError) {
        throw error
      } else {
        throw new HiddenIframeTimeout
      }
    }
  }

  /** * * * * * * * * * * * * * * * * * * * * * * * */

  // Fetch general oidc server config
  private async fetchConfig(validDomains = AUTHORITY_ORIGINS) {
    let config = LocalStorage.getItem(OIDC_CONFIG_KEY) as OidcServerConfig
    const issuer = env.OIDC_AUTHORITY

    if (isObject(config)) {
      const tokenURL = new URL(config.token_endpoint || location.origin)
      const userInfoURL = new URL(config.userinfo_endpoint || location.origin)

      if (!validDomains.includes(tokenURL.hostname) || !validDomains.includes(userInfoURL.hostname) || config.issuer !== issuer) {
        config = null as never
      }
    }

    if (!config) {
      const response = await fetch(`${this.config.authority}/${OidcClient.configEndpoint}`, { body: undefined })
      config = await response.json()

      LocalStorage.setItem(OIDC_CONFIG_KEY, config)
    }

    this.idServerConfig = Object.freeze(config)

    return config
  }

  public clear() {
    this.state?.clear()
  }

  public async signOut() {
    this.clear()
    await fetch(this.state?.config.end_session_endpoint as string, { mode: 'no-cors', credentials: 'include', redirect: 'follow' })
  }

  private async fetchProfile() {
    const state = this.state as State

    if (!state && !this.idServerConfig) {
      throw new AuthenticationError('No State')
    }

    const token = state.token as TokenData

    if (!token) throw new MissingTokenError

    const response = await fetch(this.idServerConfig?.userinfo_endpoint as string, {
      method: HTTP_METHOD.GET,
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token.access_token}`
      }
    })

    return await response.json() as UserProfile
  }

  public async user() {
    return await this.fetchProfile()
  }
}
